import type { EvidenceUploadMode, UploadEvidenceData } from 'api';
import { type DBSchema, type IDBPDatabase, openDB } from 'idb';
import { logger } from 'monitor';
import { create } from 'zustand';

import { buildEvidenceMetadataFromExif, extractExifData } from '../../utils/exif/exif';

const DB_NAME = 'offlineEvidenceDB';
const DB_VERSION = 2;
const METADATA_STORE = 'evidenceMetadata';
const FILES_STORE = 'evidenceFiles';
const DEFAULT_OFFLINE_EVIDENCE_MODE: EvidenceUploadMode = 'trade-company-session';
const DEFAULT_LEASE_DURATION_MS = 5 * 60 * 1000;

export const OFFLINE_EVIDENCE_UPLOAD_TARGET_TTL_MS = 23 * 60 * 60 * 1000;

export type OfflineEvidenceUploadStatus =
  | 'prepared'
  | 'upload_target_created'
  | 'uploading'
  | 'bytes_uploaded'
  | 'finalizing'
  | 'complete'
  | 'retryable_failed'
  | 'terminal_failed'
  | 'cleanup_pending'
  | 'deleted';

export interface OfflineEvidenceFile extends Omit<UploadEvidenceData, 'photo' | 'onProgress'> {
  bytesUploadedAt: number | null;
  offlineId: string;
  localEvidenceId: string;
  mode: EvidenceUploadMode;
  status: OfflineEvidenceUploadStatus;
  retryCount: number;
  nextRetryAt: number | null;
  leaseHeartbeatAt: number | null;
  leaseOwner: string | null;
  leaseExpiresAt: number | null;
  serverFileId: string | null;
  uploadUrl: string | null;
  uploadTargetExpiresAt: number | null;
  serverEvidenceId: number | null;
  completedAt: number | null;
  lastError: string | null;
  uploadedAt?: number;
  photoMeta: {
    name: string;
    size: number;
    type: string;
    lastModified: number;
  };
}

type PersistedOfflineEvidenceFile = Partial<OfflineEvidenceFile> &
  Pick<OfflineEvidenceFile, 'offlineId' | 'parentId' | 'photoMeta' | 'startTime'>;

type EvidenceFileBlobRecord = {
  offlineId: string;
  file: File;
};

interface OfflineEvidenceDB extends DBSchema {
  [METADATA_STORE]: {
    key: string;
    value: PersistedOfflineEvidenceFile;
    indexes: {
      status: OfflineEvidenceUploadStatus;
      'mode-parent-status': [EvidenceUploadMode, string, OfflineEvidenceUploadStatus];
    };
  };
  [FILES_STORE]: {
    key: string;
    value: EvidenceFileBlobRecord;
  };
}

interface PhotoWithObjectUrl {
  id: string;
  url: string;
}

export interface OfflineEvidenceScope {
  mode: EvidenceUploadMode;
  parentId: string | number;
}

export interface OfflineEvidenceRemovalResult {
  failedCount: number;
  offlineIds: string[];
  removedCount: number;
}

export interface OfflineEvidenceClaimScope {
  mode: EvidenceUploadMode;
  parentId?: string | number;
}

interface AddEvidenceFileOptions {
  fileId?: string;
  mode: EvidenceUploadMode;
}

interface ClaimEvidenceFilesOptions {
  activeLeaseOwners?: Iterable<string>;
  ignoreRetryBackoff?: boolean;
  leaseDurationMs?: number;
  leaseOwner: string;
  limit?: number;
  now?: number;
  reclaimAbandonedLeaseAfterMs?: number;
  scope: OfflineEvidenceClaimScope;
}

interface HeartbeatEvidenceFileLeaseOptions {
  leaseDurationMs: number;
  leaseOwner: string;
  now?: number;
  offlineId: string;
}

type OfflineEvidenceFilePatch = Partial<
  Pick<
    OfflineEvidenceFile,
    | 'bytesUploadedAt'
    | 'completedAt'
    | 'lastError'
    | 'leaseHeartbeatAt'
    | 'leaseExpiresAt'
    | 'leaseOwner'
    | 'nextRetryAt'
    | 'retryCount'
    | 'serverEvidenceId'
    | 'serverFileId'
    | 'status'
    | 'uploadedAt'
    | 'uploadTargetExpiresAt'
    | 'uploadUrl'
  >
>;

interface StoreState {
  evidenceFiles: OfflineEvidenceFile[];
  objectUrls: Record<string, PhotoWithObjectUrl[]>;

  addEvidenceFile: (file: UploadEvidenceData, options: AddEvidenceFileOptions) => Promise<string>;
  claimEvidenceFiles: (options: ClaimEvidenceFilesOptions) => Promise<OfflineEvidenceFile[]>;
  cleanupObjectUrlsByScope: (scope: OfflineEvidenceScope) => void;
  getEvidenceFiles: () => OfflineEvidenceFile[];
  getEvidenceFileById: (offlineId: string) => OfflineEvidenceFile | undefined;
  getEvidenceFilesByMode: (mode: EvidenceUploadMode) => OfflineEvidenceFile[];
  getEvidenceFilesByParentId: (parentId: string) => OfflineEvidenceFile[];
  getEvidenceFilesByScope: (scope: OfflineEvidenceScope) => OfflineEvidenceFile[];
  getPhotoFromOfflineFile: (offlineFile: OfflineEvidenceFile['offlineId']) => Promise<File | null>;
  getPhotoObjectUrlsByParentId: (parentId: string) => Promise<PhotoWithObjectUrl[]>;
  getPhotoObjectUrlsByScope: (scope: OfflineEvidenceScope) => Promise<PhotoWithObjectUrl[]>;
  markEvidenceFileUploaded: (offlineId: string) => Promise<void>;
  recoverCleanupPendingEvidenceFiles: () => Promise<void>;
  removeEvidenceFile: (offlineId: string) => Promise<void>;
  removeEvidenceFilesByScope: (scope: OfflineEvidenceScope) => Promise<OfflineEvidenceRemovalResult>;
  clearEvidenceFiles: () => Promise<void>;
  cleanupObjectUrls: (parentId: string) => void;
  heartbeatEvidenceFileLease: (options: HeartbeatEvidenceFileLeaseOptions) => Promise<OfflineEvidenceFile | null>;
  initializeStore: () => Promise<void>;
  updateEvidenceFile: (offlineId: string, patch: OfflineEvidenceFilePatch) => Promise<OfflineEvidenceFile | null>;
}

let dbPromise: Promise<IDBPDatabase<OfflineEvidenceDB>> | null = null;
let didRequestPersistentStorage = false;

const getScopeKey = ({ mode, parentId }: OfflineEvidenceScope) => `${mode}:${String(parentId)}`;

export const isEvidenceInMode = (file: OfflineEvidenceFile, mode: EvidenceUploadMode) => file.mode === mode;

export const isEvidenceInScope = (file: OfflineEvidenceFile, { mode, parentId }: OfflineEvidenceScope) =>
  String(file.parentId) === String(parentId) && file.mode === mode;

const isEvidenceInClaimScope = (file: OfflineEvidenceFile, { mode, parentId }: OfflineEvidenceClaimScope) =>
  file.mode === mode && (parentId === undefined || String(file.parentId) === String(parentId));

const hiddenStatuses = new Set<OfflineEvidenceUploadStatus>([
  'cleanup_pending',
  'complete',
  'deleted',
  'terminal_failed',
]);
const uploadEligibleStatuses = new Set<OfflineEvidenceUploadStatus>([
  'prepared',
  'upload_target_created',
  'uploading',
  'bytes_uploaded',
  'finalizing',
  'retryable_failed',
]);

const isActiveEvidenceFile = (file: OfflineEvidenceFile) => !hiddenStatuses.has(file.status);

const normalizeStatus = (file: PersistedOfflineEvidenceFile): OfflineEvidenceUploadStatus => {
  if (file.status === 'finalizing' && file.bytesUploadedAt === undefined) {
    return file.serverFileId ? 'upload_target_created' : 'prepared';
  }

  if (file.status) {
    return file.status;
  }

  return file.uploadedAt ? 'complete' : 'prepared';
};

const normalizeEvidenceFile = (file: PersistedOfflineEvidenceFile): OfflineEvidenceFile => {
  const status = normalizeStatus(file);
  const completedAt = file.completedAt ?? file.uploadedAt ?? null;

  return {
    ...file,
    bytesUploadedAt: file.bytesUploadedAt ?? null,
    completedAt,
    evidenceType: file.evidenceType,
    lastError: file.lastError ?? null,
    leaseExpiresAt: file.leaseExpiresAt ?? null,
    leaseHeartbeatAt: file.leaseHeartbeatAt ?? null,
    leaseOwner: file.leaseOwner ?? null,
    localEvidenceId: file.localEvidenceId ?? file.offlineId,
    metadata: file.metadata,
    mode: file.mode ?? DEFAULT_OFFLINE_EVIDENCE_MODE,
    model: file.model,
    nextRetryAt: file.nextRetryAt ?? null,
    offlineId: file.offlineId,
    parentId: String(file.parentId),
    photoMeta: file.photoMeta,
    processAsync: file.processAsync,
    retryCount: file.retryCount ?? 0,
    serverEvidenceId: file.serverEvidenceId ?? null,
    serverFileId: file.serverFileId ?? null,
    startTime: file.startTime,
    status,
    uploadTargetExpiresAt: file.uploadTargetExpiresAt ?? null,
    uploadUrl: file.uploadUrl ?? null,
    uploadedAt: file.uploadedAt,
  };
};

const generateOfflineId = (file: UploadEvidenceData): string =>
  `${file.photo.name}-${file.photo.size}-${file.startTime}-${Date.now()}`.replace(/\s+/g, '').trim();

const requestPersistentStorage = async (): Promise<void> => {
  if (didRequestPersistentStorage) return;
  didRequestPersistentStorage = true;

  try {
    const persist = typeof navigator !== 'undefined' ? navigator.storage?.persist : undefined;
    if (!persist) return;

    const wasPersisted = await navigator.storage.persist();
    logger.info('Offline evidence persistent browser storage requested', { wasPersisted });
  } catch (error) {
    logger.warn('Offline evidence persistent browser storage request failed', { error });
  }
};

type MetadataObjectStore = {
  createIndex: (name: keyof OfflineEvidenceDB[typeof METADATA_STORE]['indexes'], keyPath: string | string[]) => unknown;
  indexNames: { contains: (name: keyof OfflineEvidenceDB[typeof METADATA_STORE]['indexes']) => boolean };
};

const ensureMetadataIndexes = (metadataStore: MetadataObjectStore) => {
  if (!metadataStore.indexNames.contains('status')) {
    metadataStore.createIndex('status', 'status');
  }

  if (!metadataStore.indexNames.contains('mode-parent-status')) {
    metadataStore.createIndex('mode-parent-status', ['mode', 'parentId', 'status']);
  }
};

const openDatabase = (): Promise<IDBPDatabase<OfflineEvidenceDB>> => {
  if (!dbPromise) {
    dbPromise = openDB<OfflineEvidenceDB>(DB_NAME, DB_VERSION, {
      upgrade(db, _oldVersion, _newVersion, transaction) {
        const metadataStore = db.objectStoreNames.contains(METADATA_STORE)
          ? transaction.objectStore(METADATA_STORE)
          : db.createObjectStore(METADATA_STORE, { keyPath: 'offlineId' });

        ensureMetadataIndexes(metadataStore);

        if (!db.objectStoreNames.contains(FILES_STORE)) {
          db.createObjectStore(FILES_STORE, { keyPath: 'offlineId' });
        }
      },
    });
  }

  return dbPromise;
};

const storeEvidenceFile = async (metadata: OfflineEvidenceFile, file: File): Promise<void> => {
  logger.info('Persisting offline evidence file to IndexedDB', {
    fileName: metadata.photoMeta.name,
    fileSize: file.size,
    offlineId: metadata.offlineId,
    parentId: metadata.parentId,
    status: metadata.status,
  });

  await requestPersistentStorage();
  const db = await openDatabase();
  const transaction = db.transaction([METADATA_STORE, FILES_STORE], 'readwrite');

  await Promise.all([
    transaction.objectStore(METADATA_STORE).put(metadata),
    transaction.objectStore(FILES_STORE).put({ file, offlineId: metadata.offlineId }),
    transaction.done,
  ]);

  logger.info('Offline evidence file persisted to IndexedDB', {
    offlineId: metadata.offlineId,
    parentId: metadata.parentId,
  });
};

const storeEvidenceMetadata = async (metadata: OfflineEvidenceFile): Promise<void> => {
  logger.info('Persisting offline evidence metadata to IndexedDB', {
    offlineId: metadata.offlineId,
    parentId: metadata.parentId,
    status: metadata.status,
  });

  const db = await openDatabase();
  await db.put(METADATA_STORE, metadata);

  logger.info('Offline evidence metadata persisted to IndexedDB', {
    offlineId: metadata.offlineId,
    parentId: metadata.parentId,
    status: metadata.status,
  });
};

const getAllPersistedEvidenceMetadata = async (): Promise<OfflineEvidenceFile[]> => {
  const db = await openDatabase();
  const records = await db.getAll(METADATA_STORE);

  logger.info('Loaded offline evidence metadata from IndexedDB', {
    count: records.length,
  });

  return records.map(normalizeEvidenceFile);
};

const getAllActiveEvidenceMetadata = async (): Promise<OfflineEvidenceFile[]> =>
  (await getAllPersistedEvidenceMetadata()).filter(isActiveEvidenceFile);

const getFileById = async (offlineId: string): Promise<File | null> => {
  logger.debug('Loading offline evidence file blob from IndexedDB', { offlineId });
  const db = await openDatabase();
  const record = await db.get(FILES_STORE, offlineId);

  if (!record) {
    logger.warn('Offline evidence file blob was not found in IndexedDB', { offlineId });
    return null;
  }

  logger.debug('Loaded offline evidence file blob from IndexedDB', { offlineId });
  return record.file;
};

const removeEvidenceFileFromDb = async (offlineId: string): Promise<void> => {
  logger.info('Deleting offline evidence file from IndexedDB', { offlineId });
  const db = await openDatabase();
  const transaction = db.transaction([METADATA_STORE, FILES_STORE], 'readwrite');

  await Promise.all([
    transaction.objectStore(METADATA_STORE).delete(offlineId),
    transaction.objectStore(FILES_STORE).delete(offlineId),
    transaction.done,
  ]);

  logger.info('Deleted offline evidence file from IndexedDB', { offlineId });
};

const removeEvidenceFilesByScopeFromDb = async (scope: OfflineEvidenceScope): Promise<OfflineEvidenceRemovalResult> => {
  const files = (await getAllPersistedEvidenceMetadata()).filter((file) => isEvidenceInScope(file, scope));
  const results = await Promise.allSettled(files.map((file) => removeEvidenceFileFromDb(file.offlineId)));
  const offlineIds = files.filter((_, index) => results[index].status === 'fulfilled').map((file) => file.offlineId);

  return {
    failedCount: results.length - offlineIds.length,
    offlineIds,
    removedCount: offlineIds.length,
  };
};

const clearAllEvidenceFiles = async (): Promise<void> => {
  logger.info('Clearing all offline evidence files from IndexedDB');
  const db = await openDatabase();
  const transaction = db.transaction([METADATA_STORE, FILES_STORE], 'readwrite');

  await Promise.all([
    transaction.objectStore(METADATA_STORE).clear(),
    transaction.objectStore(FILES_STORE).clear(),
    transaction.done,
  ]);

  logger.info('Cleared all offline evidence files from IndexedDB');
};

const isPhotoWithObjectUrl = (photo: PhotoWithObjectUrl | null): photo is PhotoWithObjectUrl => photo !== null;

const mergeEvidenceFiles = (
  persistedFiles: PersistedOfflineEvidenceFile[],
  inMemoryFiles: PersistedOfflineEvidenceFile[]
): OfflineEvidenceFile[] => {
  const filesById = new Map<string, OfflineEvidenceFile>();

  persistedFiles
    .map(normalizeEvidenceFile)
    .filter(isActiveEvidenceFile)
    .forEach((file) => {
      filesById.set(file.offlineId, file);
    });

  inMemoryFiles
    .map(normalizeEvidenceFile)
    .filter(isActiveEvidenceFile)
    .forEach((file) => {
      filesById.set(file.offlineId, file);
    });

  return Array.from(filesById.values());
};

const isAbandonedLease = (
  file: OfflineEvidenceFile,
  now: number,
  reclaimAbandonedLeaseAfterMs: number | undefined
): boolean => {
  if (!file.leaseExpiresAt || file.leaseExpiresAt <= now) return false;
  if (reclaimAbandonedLeaseAfterMs === undefined) return false;

  if (!file.leaseHeartbeatAt) return false;

  return now - file.leaseHeartbeatAt >= reclaimAbandonedLeaseAfterMs;
};

type ClaimDecision =
  | {
      claimable: false;
      reason: 'active_lease' | 'retry_backoff' | 'status_not_uploadable';
    }
  | {
      claimable: true;
      reason: 'eligible' | 'reclaiming_abandoned_lease';
    };

const getClaimDecision = (
  file: OfflineEvidenceFile,
  now: number,
  {
    activeLeaseOwners = new Set<string>(),
    ignoreRetryBackoff = false,
    reclaimAbandonedLeaseAfterMs,
  }: {
    activeLeaseOwners?: ReadonlySet<string>;
    ignoreRetryBackoff?: boolean;
    reclaimAbandonedLeaseAfterMs?: number;
  } = {}
): ClaimDecision => {
  if (!uploadEligibleStatuses.has(file.status)) return { claimable: false, reason: 'status_not_uploadable' };
  if (!ignoreRetryBackoff && file.nextRetryAt && file.nextRetryAt > now) {
    return { claimable: false, reason: 'retry_backoff' };
  }
  if (file.leaseExpiresAt && file.leaseExpiresAt > now) {
    if (file.leaseOwner && activeLeaseOwners.has(file.leaseOwner)) {
      return { claimable: false, reason: 'active_lease' };
    }
    if (isAbandonedLease(file, now, reclaimAbandonedLeaseAfterMs)) {
      return { claimable: true, reason: 'reclaiming_abandoned_lease' };
    }
    return { claimable: false, reason: 'active_lease' };
  }
  return { claimable: true, reason: 'eligible' };
};

export const useOfflineEvidenceStore = create<StoreState>((set, get) => {
  const removeEvidenceFileFromMemory = (offlineId: string) => {
    const file = get().getEvidenceFileById(offlineId);
    const parentId = file ? String(file.parentId) : undefined;

    set((state: StoreState) => ({
      evidenceFiles: state.evidenceFiles.filter((f) => f.offlineId !== offlineId),
    }));

    const keysWithUrl = Object.entries(get().objectUrls).filter(([, urls]) => urls.some((u) => u.id === offlineId));
    if (keysWithUrl.length === 0) {
      logger.info('No offline evidence object URLs to update after removal from memory', { offlineId, parentId });
      return;
    }

    keysWithUrl.forEach(([, urls]) => {
      const url = urls.find((u) => u.id === offlineId);
      if (url) {
        URL.revokeObjectURL(url.url);
      }
    });

    set((state: StoreState) => {
      const nextObjectUrls = { ...state.objectUrls };
      keysWithUrl.forEach(([key, urls]) => {
        nextObjectUrls[key] = urls.filter((u) => u.id !== offlineId);
      });
      return { objectUrls: nextObjectUrls };
    });
    logger.info('Offline evidence object URL revoked after removal from memory', { offlineId, parentId });
  };

  const upsertEvidenceFileInMemory = (file: OfflineEvidenceFile) => {
    if (!isActiveEvidenceFile(file)) {
      removeEvidenceFileFromMemory(file.offlineId);
      return;
    }

    set((state: StoreState) => {
      const existingIndex = state.evidenceFiles.findIndex((existingFile) => existingFile.offlineId === file.offlineId);
      if (existingIndex === -1) {
        return { evidenceFiles: [...state.evidenceFiles, file] };
      }

      const evidenceFiles = [...state.evidenceFiles];
      evidenceFiles[existingIndex] = file;
      return { evidenceFiles };
    });
  };

  getAllActiveEvidenceMetadata()
    .then((metadata) => {
      set((state) => ({
        evidenceFiles: mergeEvidenceFiles(metadata, state.evidenceFiles),
      }));
    })
    .catch(() => undefined);

  return {
    addEvidenceFile: async (file: UploadEvidenceData, { fileId, mode }: AddEvidenceFileOptions) => {
      try {
        const offlineId = fileId ?? generateOfflineId(file);
        logger.info('Adding offline evidence file to store', {
          evidenceType: file.evidenceType,
          fileName: file.photo.name,
          fileSize: file.photo.size,
          offlineId,
          parentId: file.parentId,
        });
        const exifData = await extractExifData(file.photo);
        logger.info('Offline evidence EXIF extracted', {
          hasDeviceName: Boolean(exifData.deviceName),
          hasMake: Boolean(exifData.make),
          hasModel: Boolean(exifData.model),
          hasTimestamp: Boolean(exifData.timestamp),
          offlineId,
          parentId: file.parentId,
        });

        const metadata: OfflineEvidenceFile = {
          bytesUploadedAt: null,
          completedAt: null,
          evidenceType: file.evidenceType,
          lastError: null,
          leaseExpiresAt: null,
          leaseHeartbeatAt: null,
          leaseOwner: null,
          localEvidenceId: offlineId,
          metadata: buildEvidenceMetadataFromExif(exifData, file.metadata),
          mode,
          model: file.model,
          nextRetryAt: null,
          offlineId,
          parentId: String(file.parentId),
          photoMeta: {
            lastModified: file.photo.lastModified,
            name: file.photo.name,
            size: file.photo.size,
            type: file.photo.type,
          },
          processAsync: file.processAsync,
          retryCount: 0,
          serverEvidenceId: null,
          serverFileId: null,
          startTime: file.startTime,
          status: 'prepared',
          uploadTargetExpiresAt: null,
          uploadUrl: null,
        };

        await storeEvidenceFile(metadata, file.photo);
        upsertEvidenceFileInMemory(metadata);
        logger.info('Offline evidence metadata added to in-memory store', {
          offlineId,
          parentId: file.parentId,
        });

        const parentId = String(file.parentId);
        const photo = await getFileById(offlineId);
        if (photo) {
          const objectUrlKey = getScopeKey({ mode, parentId });
          const url = URL.createObjectURL(photo);
          const newPhotoUrl = { id: offlineId, url };

          const existingUrls = get().objectUrls[objectUrlKey] || [];

          set((state: StoreState) => ({
            objectUrls: {
              ...state.objectUrls,
              [objectUrlKey]: [...existingUrls.filter((existingUrl) => existingUrl.id !== offlineId), newPhotoUrl],
            },
          }));
          logger.info('Offline evidence object URL created', {
            offlineId,
            parentId,
          });
        } else {
          logger.warn('Offline evidence object URL was not created because the blob was missing', {
            offlineId,
            parentId,
          });
        }

        logger.info('Offline evidence file added to store successfully', {
          offlineId,
          parentId: file.parentId,
        });
        return offlineId;
      } catch (error) {
        logger.error('Failed to add evidence file', error);
        throw error;
      }
    },

    claimEvidenceFiles: async ({
      activeLeaseOwners = [],
      ignoreRetryBackoff = false,
      leaseDurationMs = DEFAULT_LEASE_DURATION_MS,
      leaseOwner,
      limit = Number.POSITIVE_INFINITY,
      now = Date.now(),
      reclaimAbandonedLeaseAfterMs,
      scope,
    }: ClaimEvidenceFilesOptions) => {
      const db = await openDatabase();
      const transaction = db.transaction(METADATA_STORE, 'readwrite');
      const store = transaction.objectStore(METADATA_STORE);
      const records = await store.getAll();
      const claimedFiles: OfflineEvidenceFile[] = [];
      const activeLeaseOwnerSet = new Set(activeLeaseOwners);

      for (const record of records) {
        if (claimedFiles.length >= limit) break;

        const file = normalizeEvidenceFile(record);
        const baseLogContext = {
          bytesUploadedAt: file.bytesUploadedAt,
          leaseExpiresAt: file.leaseExpiresAt,
          leaseHeartbeatAt: file.leaseHeartbeatAt,
          leaseOwner: file.leaseOwner,
          mode: file.mode,
          nextRetryAt: file.nextRetryAt,
          offlineId: file.offlineId,
          parentId: file.parentId,
          requestedMode: scope.mode,
          requestedParentId: scope.parentId,
          retryCount: file.retryCount,
          serverFileId: file.serverFileId,
          status: file.status,
        };

        if (!isEvidenceInClaimScope(file, scope)) {
          logger.debug('Offline evidence claim skipped', {
            ...baseLogContext,
            decision: 'skipped',
            reason: 'outside_scope',
          });
          continue;
        }

        const claimDecision = getClaimDecision(file, now, {
          activeLeaseOwners: activeLeaseOwnerSet,
          ignoreRetryBackoff,
          reclaimAbandonedLeaseAfterMs,
        });
        if (!claimDecision.claimable) {
          logger.debug('Offline evidence claim skipped', {
            ...baseLogContext,
            decision: 'skipped',
            ignoreRetryBackoff,
            isCurrentRuntimeLease: file.leaseOwner ? activeLeaseOwnerSet.has(file.leaseOwner) : false,
            millisecondsUntilLeaseExpires: file.leaseExpiresAt ? Math.max(file.leaseExpiresAt - now, 0) : null,
            millisecondsUntilRetry: file.nextRetryAt ? Math.max(file.nextRetryAt - now, 0) : null,
            reason: claimDecision.reason,
            reclaimAbandonedLeaseAfterMs,
          });
          continue;
        }

        const claimedFile: OfflineEvidenceFile = {
          ...file,
          leaseExpiresAt: now + leaseDurationMs,
          leaseHeartbeatAt: null,
          leaseOwner,
        };

        await store.put(claimedFile);
        claimedFiles.push(claimedFile);
        logger.debug('Offline evidence claim accepted', {
          ...baseLogContext,
          decision: 'claimed',
          ignoreRetryBackoff,
          isCurrentRuntimeLease: file.leaseOwner ? activeLeaseOwnerSet.has(file.leaseOwner) : false,
          newLeaseExpiresAt: claimedFile.leaseExpiresAt,
          newLeaseOwner: leaseOwner,
          reason: claimDecision.reason,
          reclaimAbandonedLeaseAfterMs,
        });
      }

      await transaction.done;

      claimedFiles.forEach(upsertEvidenceFileInMemory);

      logger.info('Offline evidence files claimed for sync', {
        activeLeaseOwnerCount: activeLeaseOwnerSet.size,
        count: claimedFiles.length,
        ignoreRetryBackoff,
        leaseOwner,
        mode: scope.mode,
        parentId: scope.parentId,
        reclaimAbandonedLeaseAfterMs,
      });

      return claimedFiles;
    },

    cleanupObjectUrls: (parentId: string) => {
      const keysToRemove = Object.keys(get().objectUrls).filter(
        (key) => key === parentId || key.endsWith(`:${parentId}`)
      );
      const urls = keysToRemove.flatMap((key) => get().objectUrls[key] || []);
      logger.info('Cleaning up offline evidence object URLs', {
        count: urls.length,
        parentId,
      });

      urls.forEach((photo) => {
        URL.revokeObjectURL(photo.url);
      });

      set((state: StoreState) => {
        const rest = { ...state.objectUrls };
        keysToRemove.forEach((key) => {
          delete rest[key];
        });
        return { objectUrls: rest };
      });
    },

    cleanupObjectUrlsByScope: (scope: OfflineEvidenceScope) => {
      const key = getScopeKey(scope);
      const urls = get().objectUrls[key] || [];
      logger.info('Cleaning up scoped offline evidence object URLs', {
        count: urls.length,
        mode: scope.mode,
        parentId: scope.parentId,
      });

      urls.forEach((photo) => {
        URL.revokeObjectURL(photo.url);
      });

      set((state: StoreState) => {
        const { [key]: _, ...rest } = state.objectUrls;
        return { objectUrls: rest };
      });
    },

    clearEvidenceFiles: async () => {
      try {
        logger.info('Clearing offline evidence store', {
          evidenceFilesCount: get().evidenceFiles.length,
          objectUrlParentCount: Object.keys(get().objectUrls).length,
        });
        await clearAllEvidenceFiles();

        Object.keys(get().objectUrls).forEach(get().cleanupObjectUrls);

        set({ evidenceFiles: [], objectUrls: {} });
        logger.info('Offline evidence store cleared');
      } catch (error) {
        logger.error('Failed to clear evidence files', error);
        throw error;
      }
    },

    evidenceFiles: [],

    getEvidenceFileById: (offlineId: string) => get().evidenceFiles.find((file) => file.offlineId === offlineId),

    getEvidenceFiles: () => get().evidenceFiles,

    getEvidenceFilesByMode: (mode: EvidenceUploadMode) =>
      get().evidenceFiles.filter((file) => isEvidenceInMode(file, mode)),

    getEvidenceFilesByParentId: (parentId: string) =>
      get().evidenceFiles.filter((file) => String(file.parentId) === String(parentId)),

    getEvidenceFilesByScope: (scope: OfflineEvidenceScope) =>
      get().evidenceFiles.filter((file) => isEvidenceInScope(file, scope)),

    getPhotoFromOfflineFile: async (offlineFileId: OfflineEvidenceFile['offlineId']) => {
      try {
        logger.debug('Offline evidence photo requested from store', { offlineFileId });
        return await getFileById(offlineFileId);
      } catch (error) {
        logger.error('Failed to get photo from offline file', error, {
          offlineFileId,
        });
        return null;
      }
    },

    getPhotoObjectUrlsByParentId: async (parentId: string) => {
      const stringParentId = String(parentId);
      logger.info('Offline evidence object URLs requested', { parentId: stringParentId });

      const offlineFiles = get().getEvidenceFilesByParentId(stringParentId);

      const existingUrls = get().objectUrls[stringParentId] || [];
      const existingIds = existingUrls.map((url) => url.id);

      const newFiles = offlineFiles.filter((file) => !existingIds.includes(file.offlineId));

      if (newFiles.length === 0 && existingUrls.length === offlineFiles.length && existingUrls.length > 0) {
        logger.info('Reusing existing offline evidence object URLs', {
          count: existingUrls.length,
          parentId: stringParentId,
        });
        return existingUrls;
      }

      const newPhotoUrls = await Promise.all(
        newFiles.map(async (file) => {
          const photo = await get().getPhotoFromOfflineFile(file.offlineId);
          if (photo) {
            return {
              id: file.offlineId,
              url: URL.createObjectURL(photo),
            };
          }
          return null;
        })
      );

      const validNewPhotoUrls = newPhotoUrls.filter(isPhotoWithObjectUrl);

      const allPhotoUrls = offlineFiles
        .map((file) => {
          const newUrl = validNewPhotoUrls.find((url) => url.id === file.offlineId);
          if (newUrl) return newUrl;

          const existingUrl = existingUrls.find((url) => url.id === file.offlineId);
          if (existingUrl) return existingUrl;

          logger.warn('Missing URL for offline file', {
            fileName: file.photoMeta.name,
            offlineId: file.offlineId,
          });
          return null;
        })
        .filter(isPhotoWithObjectUrl);

      logger.info('Generated photo URLs', {
        count: allPhotoUrls.length,
        existingUrlsCount: existingUrls.length,
        newUrlsCount: validNewPhotoUrls.length,
        offlineFilesCount: offlineFiles.length,
        parentId: stringParentId,
      });

      set((state: StoreState) => ({
        objectUrls: {
          ...state.objectUrls,
          [stringParentId]: allPhotoUrls,
        },
      }));

      return allPhotoUrls;
    },

    getPhotoObjectUrlsByScope: async (scope: OfflineEvidenceScope) => {
      const key = getScopeKey(scope);
      logger.info('Scoped offline evidence object URLs requested', {
        mode: scope.mode,
        parentId: scope.parentId,
      });

      const offlineFiles = get().getEvidenceFilesByScope(scope);
      const existingUrls = get().objectUrls[key] || [];
      const existingIds = existingUrls.map((url) => url.id);
      const newFiles = offlineFiles.filter((file) => !existingIds.includes(file.offlineId));

      if (newFiles.length === 0 && existingUrls.length === offlineFiles.length && existingUrls.length > 0) {
        logger.info('Reusing existing scoped offline evidence object URLs', {
          count: existingUrls.length,
          mode: scope.mode,
          parentId: scope.parentId,
        });
        return existingUrls;
      }

      const newPhotoUrls = await Promise.all(
        newFiles.map(async (file) => {
          const photo = await get().getPhotoFromOfflineFile(file.offlineId);
          if (photo) {
            return {
              id: file.offlineId,
              url: URL.createObjectURL(photo),
            };
          }
          return null;
        })
      );

      const validNewPhotoUrls = newPhotoUrls.filter(isPhotoWithObjectUrl);
      const allPhotoUrls = offlineFiles
        .map((file) => {
          const newUrl = validNewPhotoUrls.find((url) => url.id === file.offlineId);
          if (newUrl) return newUrl;

          const existingUrl = existingUrls.find((url) => url.id === file.offlineId);
          if (existingUrl) return existingUrl;

          logger.warn('Missing URL for scoped offline file', {
            fileName: file.photoMeta.name,
            offlineId: file.offlineId,
          });
          return null;
        })
        .filter(isPhotoWithObjectUrl);

      logger.info('Generated scoped photo URLs', {
        count: allPhotoUrls.length,
        existingUrlsCount: existingUrls.length,
        mode: scope.mode,
        newUrlsCount: validNewPhotoUrls.length,
        offlineFilesCount: offlineFiles.length,
        parentId: scope.parentId,
      });

      set((state: StoreState) => ({
        objectUrls: {
          ...state.objectUrls,
          [key]: allPhotoUrls,
        },
      }));

      return allPhotoUrls;
    },

    heartbeatEvidenceFileLease: async ({ leaseDurationMs, leaseOwner, now = Date.now(), offlineId }) => {
      const db = await openDatabase();
      const transaction = db.transaction(METADATA_STORE, 'readwrite');
      const store = transaction.objectStore(METADATA_STORE);
      const storedFile = await store.get(offlineId);
      const inMemoryFile = get().getEvidenceFileById(offlineId);
      const currentFile = storedFile ? normalizeEvidenceFile(storedFile) : inMemoryFile;

      if (!currentFile) {
        logger.warn('Offline evidence lease heartbeat skipped because metadata was not found', { offlineId });
        await transaction.done;
        return null;
      }

      if (currentFile.leaseOwner !== leaseOwner) {
        logger.warn('Offline evidence lease heartbeat skipped because lease owner changed', {
          currentLeaseOwner: currentFile.leaseOwner,
          expectedLeaseOwner: leaseOwner,
          offlineId,
        });
        await transaction.done;
        return null;
      }

      const updatedFile: OfflineEvidenceFile = {
        ...currentFile,
        leaseExpiresAt: now + leaseDurationMs,
        leaseHeartbeatAt: now,
        leaseOwner,
      };

      await store.put(updatedFile);
      await transaction.done;
      upsertEvidenceFileInMemory(updatedFile);
      return updatedFile;
    },

    initializeStore: async () => {
      try {
        logger.info('Offline evidence store manual initialization started');
        const metadata = await getAllActiveEvidenceMetadata();
        set((state) => ({
          evidenceFiles: mergeEvidenceFiles(metadata, state.evidenceFiles),
        }));
        logger.info('Offline evidence store manual initialization completed', { count: metadata.length });
      } catch (error) {
        logger.error('Failed to initialize store', error);
        set({ evidenceFiles: [] });
      }
    },

    markEvidenceFileUploaded: async (offlineId: string) => {
      const file = get().getEvidenceFileById(offlineId);
      if (!file) {
        logger.warn('Offline evidence uploaded marker skipped because metadata was not found', { offlineId });
        return;
      }

      const uploadedFile: OfflineEvidenceFile = {
        ...file,
        completedAt: Date.now(),
        lastError: null,
        leaseExpiresAt: null,
        leaseHeartbeatAt: null,
        leaseOwner: null,
        status: 'complete',
        uploadedAt: Date.now(),
      };
      await storeEvidenceMetadata(uploadedFile);
      removeEvidenceFileFromMemory(offlineId);
      logger.info('Offline evidence marked as uploaded and removed from active queue', {
        offlineId,
        parentId: file.parentId,
        uploadedAt: uploadedFile.uploadedAt,
      });
    },

    objectUrls: {},

    recoverCleanupPendingEvidenceFiles: async () => {
      // Records whose upload completed server-side but whose local IndexedDB cleanup failed get
      // parked in 'cleanup_pending' — hidden from the UI and never re-claimed by sync. Re-attempt
      // their removal so the blob + metadata don't leak forever.
      const persisted = await getAllPersistedEvidenceMetadata();
      const pending = persisted.filter((file) => file.status === 'cleanup_pending');
      if (pending.length === 0) {
        return;
      }

      logger.info('Recovering cleanup_pending offline evidence files', {
        count: pending.length,
        offlineIds: pending.map((file) => file.offlineId),
      });
      for (const file of pending) {
        try {
          await get().removeEvidenceFile(file.offlineId);
        } catch (error) {
          logger.warn('Failed to recover cleanup_pending offline evidence; will retry next sync', {
            error: error instanceof Error ? error.message : String(error),
            offlineId: file.offlineId,
          });
        }
      }
    },

    removeEvidenceFile: async (offlineId: string) => {
      logger.info('Removing offline evidence file from store', { offlineId });
      try {
        const file = get().getEvidenceFileById(offlineId);
        if (!file) {
          logger.warn('Offline evidence file metadata was not found before IndexedDB removal', { offlineId });
        }

        await removeEvidenceFileFromDb(offlineId);
        removeEvidenceFileFromMemory(offlineId);
        logger.info('Offline evidence metadata removed from in-memory store', {
          offlineId,
          parentId: file?.parentId,
        });
      } catch (error) {
        logger.error('Failed to remove evidence file', error);
        throw error;
      }
    },

    removeEvidenceFilesByScope: async (scope: OfflineEvidenceScope) => {
      logger.info('Removing scoped offline evidence files from store', scope);
      try {
        const result = await removeEvidenceFilesByScopeFromDb(scope);
        result.offlineIds.forEach(removeEvidenceFileFromMemory);

        if (result.failedCount > 0) {
          throw new Error(`Failed to clear ${result.failedCount} offline evidence files`);
        }

        logger.info('Scoped offline evidence files removed from store', {
          ...scope,
          removedCount: result.removedCount,
        });
        return result;
      } catch (error) {
        logger.error('Failed to remove scoped offline evidence files', error, { ...scope });
        throw error;
      }
    },

    updateEvidenceFile: async (offlineId: string, patch: OfflineEvidenceFilePatch) => {
      const db = await openDatabase();
      const storedFile = await db.get(METADATA_STORE, offlineId);
      const inMemoryFile = get().getEvidenceFileById(offlineId);
      const currentFile = storedFile ? normalizeEvidenceFile(storedFile) : inMemoryFile;

      if (!currentFile) {
        logger.warn('Offline evidence update skipped because metadata was not found', { offlineId, patch });
        return null;
      }

      const updatedFile: OfflineEvidenceFile = {
        ...currentFile,
        ...patch,
      };

      await storeEvidenceMetadata(updatedFile);
      upsertEvidenceFileInMemory(updatedFile);
      return updatedFile;
    },
  };
});
